import { createElement } from '../../../core/dom/helpers.js';
import { asyncSelectDriver } from './async-select-driver.js';

/**
 * Form-link driver — the article's "Form" field. Composes the async-select driver (for picking an
 * existing saved form) and adds a "Create new form" button that opens the form-builder modal via
 * the injected `ctx.formPicker`, then auto-selects the created form.
 *
 * Design (see docs/form-builder.md):
 * - **Single display owner**: the inner async-select owns the trigger/value label. This driver owns
 *   only a `value→label` cache (so a freshly-created form's name paints with no network round-trip)
 *   and the create button. The cache is consulted by a WRAPPED `resolveLabel` on the inner def;
 *   `loadOptions` is wrapped to record `{value→label}` as options arrive.
 * - **Graceful degradation**: if `ctx.formPicker` is absent, the create button is hidden and the
 *   field is select-existing-only (never calls `.open` on undefined).
 * - **Race safety**: the open create session is tracked by an AbortController (`activeSession`).
 *   A stale resolution commits ONLY if it's still the active session. The session is aborted before
 *   ANY competing value change — a real inner-select change (pick/clear) AND host-driven
 *   `setValue`/`clearValue` — so an abandoned create modal can never overwrite a newer/reset value.
 *
 * @type {import('../../../contracts/forms.js').FieldDriver}
 */
export const formLinkDriver = {
  type: 'form-link',
  /**
   * @param {HTMLElement} container
   * @param {import('../../../contracts/forms.js').FieldDriverContext} ctx
   * @returns {import('../../../contracts/forms.js').FieldDriverHandle}
   */
  mount(container, ctx) {
    const def = /** @type {import('../../../contracts/forms.js').FormLinkFieldDef} */ (ctx.def);
    const { formPicker } = ctx;

    /** value(string) → label cache. Seeded on create + populated as options load. */
    const labelCache = new Map();

    const wrapper = createElement('div', { class: 'form-link' });
    const selectHost = createElement('div', { class: 'form-link__select' });
    wrapper.appendChild(selectHost);

    let disabled = Boolean(def.disabled);
    /** Current value in normalized (string) form — for reference-stable emit suppression. */
    let currentKey = ctx.value != null ? String(ctx.value) : '';

    /** @type {AbortController | null} The in-flight create session (if any). */
    let activeSession = null;
    /** @type {(() => void) | null} The open modal's teardown handle, if any. */
    let activeModalDestroy = null;
    /** Abort + tear down the active create session so its late resolution is dropped. */
    function abortSession() {
      if (activeSession) { activeSession.abort(); activeSession = null; }
      if (activeModalDestroy) { activeModalDestroy(); activeModalDestroy = null; }
    }

    // --- Inner async-select: wrap loadOptions + resolveLabel to feed/consult the cache ---

    const baseResolveLabel = def.resolveLabel;
    const baseLoadOptions = def.loadOptions;

    /** @param {import('../../../contracts/forms.js').FieldOption[]} options */
    function cacheOptions(options) {
      for (const o of options) {
        if (o && o.value != null) labelCache.set(String(o.value), o.label ?? '');
      }
    }

    /** @type {import('../../../contracts/forms.js').AsyncSelectFieldDef} */
    const innerDef = /** @type {*} */ ({
      ...def,
      type: 'async-select',
      // Cache-first label resolution: a freshly-created (or already-seen) form resolves with no
      // network call; otherwise delegate to the factory's batched by-id lookup.
      /** @param {unknown} value */
      resolveLabel: (value) => {
        const key = String(value ?? '');
        if (labelCache.has(key)) return Promise.resolve(labelCache.get(key));
        return baseResolveLabel ? baseResolveLabel(value) : Promise.resolve(`#${key}`);
      },
      loadOptions: baseLoadOptions
        ? /** @param {import('../../../contracts/forms.js').OptionLoaderContext} loadCtx */ async (loadCtx) => {
            const options = await baseLoadOptions(loadCtx);
            cacheOptions(options);
            return options;
          }
        : undefined,
    });

    // The inner select forwards value changes through this onChange. ANY real change here is a
    // competing selection, so abort the active create session before forwarding.
    const innerHandle = /** @type {import('../../../contracts/forms.js').FieldDriverHandle} */ (
      asyncSelectDriver.mount(selectHost, /** @type {*} */ ({
        ...ctx,
        def: innerDef,
        /** @param {unknown} value */
        onChange: (value) => {
          abortSession();
          const key = value != null ? String(value) : '';
          if (key === currentKey) return; // reference-stable: reselecting the same form is a no-op
          currentKey = key;
          ctx.onChange(value);
        },
      }))
    );

    // --- "Create new form" button (only when the capability is present) ---

    /** @type {HTMLButtonElement | null} */
    let createBtn = null;
    if (formPicker) {
      createBtn = /** @type {HTMLButtonElement} */ (createElement('button', {
        type: 'button', class: 'form-link__create btn btn-secondary',
      }, ['Create new form']));
      createBtn.disabled = disabled;
      createBtn.addEventListener('click', () => {
        if (disabled) return;
        openCreate();
      });
      wrapper.appendChild(createBtn);
    }

    /** Open the create/copy-from modal; on a fresh, current resolution, select the new form. */
    function openCreate() {
      if (!formPicker) return;
      abortSession(); // a new create supersedes any prior open session
      const controller = new AbortController();
      activeSession = controller;
      const { promise, destroy } = formPicker.open({ signal: controller.signal });
      activeModalDestroy = destroy;
      promise.then((result) => {
        // Commit only if THIS session is still the active one and wasn't aborted by a
        // competing change (inner-select pick/clear, host reset, or a newer create click).
        if (controller !== activeSession || controller.signal.aborted) return;
        activeSession = null;
        activeModalDestroy = null; // resolved cleanly — nothing left to tear down
        if (!result) return; // cancelled
        const id = String(result.id);
        labelCache.set(id, result.form_name ?? ''); // seed BEFORE setValue so the label paints
        innerHandle.setValue(result.id);
        if (id !== currentKey) {
          currentKey = id;
          ctx.onChange(result.id); // a created form always has a fresh id, but guard anyway
        }
      }).catch(() => {
        if (controller === activeSession) { activeSession = null; activeModalDestroy = null; }
      });
    }

    container.appendChild(wrapper);

    return {
      getValue() {
        return innerHandle.getValue();
      },
      setValue(value) {
        abortSession(); // a host-driven reset must not be overwritten by a late create
        currentKey = value != null ? String(value) : '';
        innerHandle.setValue(value);
      },
      clearValue() {
        abortSession();
        currentKey = '';
        if (typeof innerHandle.clearValue === 'function') innerHandle.clearValue();
        else innerHandle.setValue('');
      },
      setDisabled(d) {
        disabled = d;
        if (d) abortSession(); // disabling mid-create closes the modal (a disabled field can't commit)
        if (createBtn) createBtn.disabled = d;
        innerHandle.setDisabled?.(d);
      },
      setVisible(visible) {
        if (!visible) abortSession(); // hiding mid-create closes the modal
        wrapper.hidden = !visible;
      },
      setErrors() {
        // errors rendered by the host wrapper
      },
      setAriaInvalid(invalid) {
        innerHandle.setAriaInvalid?.(invalid);
      },
      getOptionsProvider() {
        // Delegate so dependent-option reload keeps working if this field ever uses dependsOn.
        return innerHandle.getOptionsProvider?.();
      },
      destroy() {
        abortSession();
        innerHandle.destroy();
        wrapper.remove();
      },
    };
  },
};
